import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DeleteAccountPanel from "@/components/DeleteAccountPanel";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import UploadButton from "@/components/UploadButton";
import ZipInput from "@/components/ZipInput";
import { User, Bell, CreditCard, Sparkles, ExternalLink, Check, ShieldCheck, EyeOff, VolumeX, Ban, Mail, Lock, MessageCircleHeart, Link2, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";

const SECTIONS = (role) => {
  if (role === "pro") {
    return [
      { key: "account", label: "Account", Icon: User },
      { key: "notifications", label: "Notifications", Icon: Bell },
      { key: "billing", label: "Plan & Billing", Icon: CreditCard },
      { key: "safety", label: "Privacy & Safety", Icon: ShieldCheck },
    ];
  }
  if (role === "client") {
    return [
      { key: "account", label: "Account", Icon: User },
      { key: "contact", label: "Contact", Icon: Mail },
      { key: "privacy", label: "Privacy", Icon: ShieldCheck },
      { key: "notifications", label: "Notifications", Icon: Bell },
      { key: "blocked", label: "Blocked", Icon: Ban },
      { key: "security", label: "Security", Icon: Lock },
      { key: "feedback", label: "Feedback", Icon: MessageCircleHeart },
    ];
  }
  return [
    { key: "account", label: "Account", Icon: User },
    { key: "notifications", label: "Notifications", Icon: Bell },
    { key: "safety", label: "Privacy & Safety", Icon: ShieldCheck },
  ];
};

const DEFAULT_PRIVACY = { show_wall_posts: true, show_recommendations: true, show_boards: false, show_connections: false };

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("account");
  const [form, setForm] = useState({ name: "", bio: "", avatar_url: "" });
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mod, setMod] = useState(null);
  const [reports, setReports] = useState([]);
  const [clientPrefs, setClientPrefs] = useState({ open_to_contacts: false, looking_for: "" });
  const [privacy, setPrivacy] = useState(DEFAULT_PRIVACY);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [program, setProgram] = useState(null);

  const loadSafety = () => {
    api.get("/moderation/me").then(r => setMod(r.data)).catch(() => {});
    api.get("/moderation/my-reports").then(r => setReports(r.data.reports || [])).catch(() => {});
  };
  const unhide = async (id) => { await api.delete(`/moderation/hide/${id}`); loadSafety(); toast.success("Post restored"); };
  const unmute = async (id) => { await api.delete(`/moderation/mute/${id}`); loadSafety(); toast.success("Unmuted"); };
  const unblock = async (id) => { await api.delete(`/moderation/block/${id}`); loadSafety(); toast.success("Unblocked"); };

  useEffect(() => {
    if (user === null) return;
    if (!user) { navigate("/login"); return; }
    setForm({ name: user.name || "", bio: user.bio || "", avatar_url: user.avatar_url || "", zip: user.zip || "" });
    api.get("/users/me/email-prefs").then(r => setEmailPrefs(r.data)).catch(() => {});
    api.get("/config/public").then(r => { setPricing(r.data?.pro_plus_pricing || null); setProgram(r.data?.pro_plus_program || null); }).catch(() => {});
    loadSafety();
    if (user.role === "client") {
      setClientPrefs({ open_to_contacts: !!user.open_to_contacts, looking_for: user.looking_for || "" });
      setPrivacy({ ...DEFAULT_PRIVACY, ...(user.client_privacy || {}) });
    }
    // eslint-disable-next-line
  }, [user, navigate]);

  if (user === null) return <div className="min-h-screen grid place-items-center"><p>Loading…</p></div>;
  if (!user) return null;

  const isPro = user.role === "pro";
  const enrollmentPaused = program ? program.enrollment_enabled === false : false;
  const isClient = user.role === "client";

  const saveAccount = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name: form.name, bio: form.bio, avatar_url: form.avatar_url, zip: form.zip });
      await refresh();
      toast.success("Account updated");
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const onAvatar = async (url) => {
    setForm(f => ({ ...f, avatar_url: url }));
    try {
      await api.patch("/users/me", { avatar_url: url });
      await refresh();
      toast.success("Photo updated");
    } catch { toast.error("Could not update photo"); }
  };

  const toggleEmailPref = async (key, value) => {
    try {
      const r = await api.patch("/users/me/email-prefs", { [key]: value });
      setEmailPrefs(r.data);
      toast.success("Preferences saved");
    } catch { toast.error("Could not update preferences"); }
  };

  const saveClientPrefs = async () => {
    setBusy(true);
    try { await api.patch("/clients/me/prefs", clientPrefs); await refresh(); toast.success("Contact preferences saved"); }
    catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const togglePrivacy = async (key, value) => {
    setPrivacy(p => ({ ...p, [key]: value }));
    try { const r = await api.patch("/clients/me/privacy", { [key]: value }); setPrivacy(r.data); toast.success("Privacy updated"); }
    catch { toast.error("Could not update privacy"); }
  };

  const changePassword = async () => {
    if (pwForm.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    setPwBusy(true);
    try {
      await api.patch("/users/me/password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update password"); }
    finally { setPwBusy(false); }
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Billing portal is unavailable until a subscription is active.");
    }
  };

  const cancelPlan = async () => {
    if (!window.confirm(
      "Cancel Pro Plus?\n\n• You keep every Pro Plus feature until the end of the period you've already paid for\n"
      + "• No further renewal payments will be taken\n• No cancellation fee, and no reason needed\n"
      + "• Your profile, posts, reviews and messages are NOT deleted — you simply return to Free Pro\n"
      + "• Payments already made aren't ordinarily refunded, including unused time, except where the law requires it"
    )) return;
    try {
      const r = await api.post("/payments/cancel");
      await refresh();
      const ends = (r.data?.access_ends_at || "").slice(0, 10);
      toast.success(ends
        ? `Cancelled. Pro Plus stays active until ${ends}, then your account returns to Free Pro. Nothing is deleted.`
        : "Cancelled. You keep Pro Plus until the end of the paid period, then return to Free Pro.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const prefRows = isPro
    ? [
        { k: "new_message", label: "New messages", desc: "When a neighbor messages you" },
        { k: "new_hire", label: "New hire requests", desc: "When someone requests to hire you" },
        { k: "hire_update", label: "Hire updates", desc: "Accepted, completed or cancelled jobs" },
        { k: "review_received", label: "New reviews", desc: "When a client leaves you a review" },
        { k: "weekly_digest", label: "Weekly engagement digest", desc: "A recap of reactions & comments" },
      ]
    : [
        { k: "new_message", label: "New messages", desc: "When a pro messages you" },
        { k: "hire_update", label: "Hire updates", desc: "Accepted, completed or cancelled jobs" },
        { k: "worked_together", label: "Worked Together confirmations", desc: "When a pro confirms you worked together" },
        { k: "request_expiring", label: "Request reminders", desc: "A heads-up before one of your requests expires" },
      ];

  const proPlusOpen = !!program?.enrollment_enabled;

  const Toggle = ({ checked, onChange, testId, disabled = false }) => (
    <div className="relative">
      <input type="checkbox" className="sr-only" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} data-testid={testId}/>
      <div className={`w-11 h-6 rounded-full transition ${checked ? "bg-[#2E6B57]" : "bg-black/15"}`}>
        <div className={`w-5 h-5 bg-white rounded-full shadow-md transition transform mt-0.5 ${checked ? "translate-x-5" : "translate-x-0.5"}`}/>
      </div>
    </div>
  );

  return (
    <div>
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Settings</h1>
        <p className="text-[#222]/60 mb-8">Manage your account, notifications{isPro ? ", and subscription" : ""}.</p>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
          {/* Left nav */}
          <aside className="space-y-1 md:sticky md:top-20 md:self-start" data-testid="settings-nav">
            {SECTIONS(user.role).map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${tab === key ? "bg-[#222] text-white" : "text-[#222]/70 hover:bg-black/5"}`}
                data-testid={`settings-tab-${key}`}>
                <Icon size={16}/> {label}
              </button>
            ))}
          </aside>

          {/* Content */}
          <div>
            {tab === "account" && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 space-y-5" data-testid="settings-account">
                <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Account</h2>
                <div className="flex items-center gap-4">
                  {form.avatar_url
                    ? <img src={form.avatar_url} className="w-16 h-16 rounded-full object-cover" alt=""/>
                    : <div className="w-16 h-16 rounded-full bg-[#2E6B57] grid place-items-center text-2xl font-bold">{form.name?.[0] || "?"}</div>}
                  <UploadButton onUploaded={onAvatar} label="Change photo"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Full name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="settings-name-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Bio</label>
                  <textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="settings-bio-input"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">Email</label>
                  <input value={user.email} disabled
                    className="w-full mt-1 px-3 py-2 border border-black/10 rounded-xl text-sm bg-[#F2F4F2] text-[#222]/50" data-testid="settings-email-input"/>
                  <p className="text-xs text-[#222]/40 mt-1">Contact support to change your email.</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#222]/60 font-semibold">ZIP code (for the Wall)</label>
                  <div className="mt-1">
                    <ZipInput value={form.zip} onChange={v => setForm({ ...form, zip: v })}
                      placeholder="e.g. 11201" testId="settings-zip-input"
                      inputClassName="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"/>
                  </div>
                  <p className="text-xs text-[#222]/40 mt-1">Used to show you nearby Wall posts. Never shown publicly.</p>
                </div>
                {isClient && (
                  <button onClick={() => navigate(`/client/${user.id}`)} className="text-xs text-[#245544] font-semibold flex items-center gap-1" data-testid="settings-view-public-profile"><ExternalLink size={12}/> View your public profile</button>
                )}
                <button onClick={saveAccount} disabled={busy} className="btn-lime px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="settings-account-save">
                  <Check size={15}/> Save changes
                </button>

                <DeleteAccountPanel/>
              </div>
            )}

            {tab === "contact" && isClient && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="settings-contact">
                <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Contact preferences</h2>
                <p className="text-sm text-[#222]/60 mb-4" data-testid="contact-pref-subtitle">Choose whether Pro Plus professionals can reach out when you're looking for help.</p>
                {!proPlusOpen && (
                  <div className="bg-[#F5F2EB] border border-black/[0.07] rounded-xl px-4 py-3 mb-4 text-xs text-[#222]/70" data-testid="contact-pref-unavailable">
                    Pro Plus hasn't launched yet, so no professional can message you this way. This setting will become available at launch.
                  </div>
                )}
                <label className={`flex items-center gap-3 mb-3 ${proPlusOpen ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}>
                  <Toggle checked={clientPrefs.open_to_contacts} disabled={!proPlusOpen}
                    onChange={v => proPlusOpen && setClientPrefs({ ...clientPrefs, open_to_contacts: v })} testId="client-open-to-contacts-toggle"/>
                  <span className="text-sm font-medium">Allow Pro Plus professionals to contact me</span>
                </label>
                {clientPrefs.open_to_contacts && proPlusOpen && (
                  <input value={clientPrefs.looking_for} onChange={e => setClientPrefs({ ...clientPrefs, looking_for: e.target.value })}
                    placeholder="What are you looking for? (e.g. 'Plumber for leaky faucet, Brooklyn')"
                    className="w-full mt-2 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
                    data-testid="client-looking-for-input"/>
                )}
                <button onClick={saveClientPrefs} disabled={busy || !proPlusOpen} className="btn-lime mt-4 px-5 py-2 text-sm disabled:opacity-50" data-testid="client-save-prefs-btn">Save preferences</button>
              </div>
            )}

            {tab === "privacy" && isClient && (
              <div className="space-y-6" data-testid="settings-privacy">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="settings-privacy-explainer">
                  <h2 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Link2 size={18}/> Public profile visibility</h2>
                  <p className="text-sm text-[#222]/60">Your public profile at /client/{user.id} shows only the posts you choose to publish there — photos and videos you share from your profile. Neighbors can follow you to see new ones in their feed.</p>
                  <ul className="mt-4 space-y-2 text-sm text-[#222]/70">
                    <li className="flex gap-2"><span className="text-[#2E6B57] font-bold">·</span> My Board stays private unless you share it yourself.</li>
                    <li className="flex gap-2"><span className="text-[#2E6B57] font-bold">·</span> Your Wall history lives in Activity &amp; Saved — only you can see it.</li>
                    <li className="flex gap-2"><span className="text-[#2E6B57] font-bold">·</span> Requests, messages and your contact details are never on your profile.</li>
                    <li className="flex gap-2"><span className="text-[#2E6B57] font-bold">·</span> Pros can't browse neighbor profiles.</li>
                  </ul>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><EyeOff size={16}/> Hidden &amp; muted</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><EyeOff size={15}/> Hidden posts ({mod?.hidden_posts?.length || 0})</p>
                      {(mod?.hidden_posts || []).map(p => (
                        <div key={p.id} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5" data-testid={`hidden-${p.id}`}>
                          <span className="truncate text-[#222]/70">{p.content || "Post"}</span>
                          <button onClick={() => unhide(p.id)} className="text-[#245544] font-semibold shrink-0 ml-3">Restore</button>
                        </div>
                      ))}
                      {!mod?.hidden_posts?.length && <p className="text-xs text-[#222]/40">Nothing hidden.</p>}
                    </div>
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><VolumeX size={15}/> Muted accounts ({mod?.muted?.length || 0})</p>
                      {(mod?.muted || []).map(m => (
                        <div key={m.user_id} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5" data-testid={`muted-${m.user_id}`}>
                          <span>{m.name}</span><button onClick={() => unmute(m.user_id)} className="text-[#245544] font-semibold">Unmute</button>
                        </div>
                      ))}
                      {!mod?.muted?.length && <p className="text-xs text-[#222]/40">No muted accounts.</p>}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                  <h3 className="font-bold text-lg mb-3" style={{ fontFamily: "Outfit" }}>Reports you submitted</h3>
                  {reports.length === 0 ? <p className="text-xs text-[#222]/40">You haven't reported anything.</p> : (
                    <div className="space-y-2">
                      {reports.map(r => (
                        <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-black/5" data-testid={`report-${r.id}`}>
                          <span className="capitalize">{r.reason.replace("_", " ")} · {r.target_type}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-black/5 capitalize">{(r.status || "submitted").replace("_", " ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "notifications" && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="settings-notifications">
                <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Email notifications</h2>
                <p className="text-sm text-[#222]/60 mb-4">Only high-signal emails. Billing &amp; security always send.</p>
                {emailPrefs ? (
                  <div className="divide-y divide-black/5">
                    {prefRows.map(p => (
                      <label key={p.k} className="flex items-center justify-between py-4 cursor-pointer">
                        <div>
                          <p className="text-sm font-semibold">{p.label}</p>
                          <p className="text-xs text-[#222]/55">{p.desc}</p>
                        </div>
                        <Toggle checked={!!emailPrefs[p.k]} onChange={v => toggleEmailPref(p.k, v)} testId={`settings-pref-${p.k}`}/>
                      </label>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#222]/50">Loading…</p>}
              </div>
            )}

            {tab === "blocked" && isClient && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="settings-blocked">
                <h2 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Ban size={18}/> Blocked accounts</h2>
                <p className="text-sm text-[#222]/60 mb-4">Blocked pros can't message you, and neither of you sees the other's posts.</p>
                {(mod?.blocked || []).map(m => (
                  <div key={m.user_id} className="flex items-center justify-between py-2 text-sm border-b border-black/5" data-testid={`blocked-${m.user_id}`}>
                    <span>{m.name}</span><button onClick={() => unblock(m.user_id)} className="text-[#245544] font-semibold">Unblock</button>
                  </div>
                ))}
                {!mod?.blocked?.length && <p className="text-xs text-[#222]/40">No blocked accounts. Block a pro from the "…" menu on any of their posts or from a message thread.</p>}
              </div>
            )}

            {tab === "security" && isClient && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 max-w-md" data-testid="settings-security">
                <h2 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Lock size={18}/> Password</h2>
                <p className="text-sm text-[#222]/60 mb-4">Change your password. You'll stay signed in on this device.</p>
                <div className="space-y-3">
                  <input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                    placeholder="Current password" className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="settings-pw-current"/>
                  <input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                    placeholder="New password (min. 6 characters)" className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="settings-pw-new"/>
                  <input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
                    placeholder="Confirm new password" className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="settings-pw-confirm"/>
                </div>
                <button onClick={changePassword} disabled={pwBusy} className="btn-lime mt-4 px-5 py-2 text-sm disabled:opacity-50" data-testid="settings-pw-save">Update password</button>
              </div>
            )}

            {tab === "feedback" && isClient && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="settings-feedback">
                <h2 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><MessageCircleHeart size={18}/> Feedback</h2>
                <p className="text-sm text-[#222]/60 mb-4">Spotted a bug or have an idea? Tell us — every submission gets a reference number.</p>
                <button onClick={() => navigate("/feedback")} className="btn-lime px-5 py-2 text-sm" data-testid="settings-open-feedback">Open feedback form</button>
              </div>
            )}

            {tab === "billing" && isPro && (
              <div className="space-y-6" data-testid="settings-billing">
                {!user.is_pro_plus && user.pro_plus_source === "trial" && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#2E6B57]/30" data-testid="billing-trial-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#222]/50 font-semibold mb-1">Current plan</p>
                        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                          <Sparkles size={20} className="text-[#245544]"/> Pro Plus — Complimentary Trial
                        </h2>
                      </div>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#E9F0EC] text-[#245544]" data-testid="billing-trial-status">
                        {user.trial?.trial_days_remaining != null ? `${user.trial.trial_days_remaining} day${user.trial.trial_days_remaining === 1 ? "" : "s"} left` : "Trial"}
                      </span>
                    </div>
                    <ul className="text-sm text-[#222]/70 mt-4 space-y-1.5 list-disc list-inside">
                      <li>Every Pro Plus feature is switched on for your trial.</li>
                      <li>No payment method is required — you have not entered a card.</li>
                      <li>You will not be automatically charged.</li>
                      <li>Your trial ends {user.trial?.trial_ends_at ? new Date(user.trial.trial_ends_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "soon"}, after which you'll return to Free Pro unless you subscribe.</li>
                    </ul>
                    <button onClick={() => navigate("/upgrade")} className="btn-lime mt-4 px-5 py-2 text-sm" data-testid="billing-trial-subscribe-btn">
                      {enrollmentPaused
                        ? "See what's coming with Pro Plus"
                        : pricing?.available ? `Upgrade to Pro Plus — ${pricing.display}` : "See Pro Plus plans"}
                    </button>
                  </div>
                )}
                {!user.is_pro_plus && user.pro_plus_source === "beta" && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#2E6B57]/30" data-testid="billing-beta-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#222]/50 font-semibold mb-1">Current plan</p>
                        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                          <Sparkles size={20} className="text-[#245544]"/> Pro Plus — Complimentary Beta Access
                        </h2>
                      </div>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#E9F0EC] text-[#245544]" data-testid="billing-beta-status">Beta</span>
                    </div>
                    <ul className="text-sm text-[#222]/70 mt-4 space-y-1.5 list-disc list-inside">
                      <li>You're currently receiving complimentary beta access to Pro Plus features.</li>
                      <li>No payment method is required — you have not entered a card.</li>
                      <li>You will not be automatically charged.</li>
                      <li>You'll return to Free Pro when the beta ends, unless you choose to subscribe.</li>
                    </ul>
                    <button onClick={() => navigate("/upgrade")} className="btn-lime mt-4 px-5 py-2 text-sm" data-testid="billing-beta-subscribe-btn">
                      {pricing?.available ? `Upgrade to Pro Plus — ${pricing.display}` : "See Pro Plus plans"}
                    </button>
                  </div>
                )}
                {user.is_pro_plus ? (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#222]/50 font-semibold mb-1">Current plan</p>
                        <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                          <Sparkles size={22} className="text-[#2E6B57]"/> Pro Plus
                        </h2>
                      </div>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${user.pro_plus_status === "past_due" ? "bg-amber-100 text-amber-800" : user.pro_plus_cancel_at_period_end ? "bg-black/10 text-[#222]/70" : "bg-[#E9F0EC] text-[#245544]"}`} data-testid="billing-status">
                        {user.pro_plus_status === "past_due" ? "Past due" : user.pro_plus_cancel_at_period_end ? "Ending soon" : "Active"}
                      </span>
                    </div>

                    {user.pro_plus_expires_at && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                        <div className="bg-[#F2F4F2] rounded-xl p-4 border border-black/5">
                          <p className="text-xs uppercase tracking-wider text-[#222]/50 font-semibold">{user.pro_plus_cancel_at_period_end ? "Access until" : "Next payment"}</p>
                          <p className="text-sm font-semibold mt-1">{new Date(user.pro_plus_expires_at).toLocaleDateString()}</p>
                        </div>
                        <div className="bg-[#F2F4F2] rounded-xl p-4 border border-black/5 flex items-center justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-[#222]/50 font-semibold">Payment method</p>
                            <p className="text-sm font-semibold mt-1">Managed by Stripe</p>
                          </div>
                          <button onClick={openPortal} className="text-xs text-[#245544] font-semibold flex items-center gap-1" data-testid="billing-update-method">Update <ExternalLink size={12}/></button>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mt-5">
                      <button onClick={openPortal} className="btn-dark px-5 py-2.5 text-sm flex items-center gap-2" data-testid="billing-portal-btn">
                        <ExternalLink size={15}/> Manage in Stripe portal
                      </button>
                      {!user.pro_plus_cancel_at_period_end && (
                        <button onClick={cancelPlan} className="px-5 py-2.5 text-sm rounded-full border border-red-200 text-red-600 hover:bg-red-50" data-testid="billing-cancel-btn">Cancel Pro Plus</button>
                      )}
                    </div>
                    {user.pro_plus_cancel_at_period_end ? (
                      <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="billing-cancel-status">
                        <p className="text-sm font-semibold text-amber-900">Cancellation scheduled</p>
                        <p className="text-xs text-amber-900/80 mt-0.5">
                          Pro Plus stays active until{" "}
                          <b data-testid="billing-access-ends">{(user.pro_plus_expires_at || "").slice(0, 10) || "the end of the paid period"}</b>,
                          then your account returns to Free Pro. Nothing is deleted, and no further payments will be taken.
                          You can reactivate any time before then.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-[#222]/50 mt-3">
                        Cancelling keeps Pro Plus until{" "}
                        <b>{(user.pro_plus_expires_at || "").slice(0, 10) || "the end of the paid period"}</b>, then returns you to
                        Free Pro. No fee, no reason needed, and nothing is deleted.
                      </p>
                    )}
                  </div>
                ) : user.pro_plus_source === "trial" ? null : (
                  <div className="bg-gradient-to-br from-[#222] to-[#444] text-white rounded-2xl p-8 shadow-lg">
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-1 text-white/60">Current plan</p>
                    <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Free Pro</h2>
                    <p className="text-white/70 text-sm mb-6 max-w-md">Upgrade to Pro Plus for homepage placement, priority ranking, advanced analytics and outbound messaging to clients who are looking.</p>
                    {enrollmentPaused ? (
                      <>
                        <button disabled className="px-7 py-3 rounded-full bg-white/10 text-white/50 font-bold cursor-not-allowed flex items-center gap-2" data-testid="billing-coming-soon-btn">
                          <Clock size={16}/> Coming Soon
                        </button>
                        <p className="text-xs text-white/50 mt-3">Pro Plus isn't open for enrollment just yet — we'll let you know the moment it goes live.</p>
                      </>
                    ) : (
                      <button onClick={() => navigate("/upgrade")} className="btn-lime px-7 py-3 flex items-center gap-2" data-testid="billing-upgrade-btn">
                        <Sparkles size={16}/> Upgrade to Pro Plus{pricing?.available ? ` — ${pricing.display}` : ""}
                      </button>
                    )}
                  </div>
                )}

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="billing-history">
                  <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "Outfit" }}>Billing history</h3>
                  <p className="text-sm text-[#222]/60">
                    Your full invoice history and receipts are available in the secure Stripe billing portal.
                    {" "}Pricing, renewal and cancellation are covered in the{" "}
                    <a href="/legal/membership" target="_blank" rel="noreferrer" className="text-[#245544] font-semibold underline" data-testid="settings-membership-terms-link">Membership &amp; Billing Terms</a>.
                  </p>
                  {user.is_pro_plus && (
                    <button onClick={openPortal} className="mt-3 text-sm text-[#245544] font-semibold flex items-center gap-1" data-testid="billing-history-portal">
                      View invoices <ExternalLink size={13}/>
                    </button>
                  )}
                </div>
              </div>
            )}

            {tab === "safety" && !isClient && (
              <div className="space-y-6" data-testid="settings-safety">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                  <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>What you've hidden</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><EyeOff size={15}/> Hidden posts ({mod?.hidden_posts?.length || 0})</p>
                      {(mod?.hidden_posts || []).map(p => (
                        <div key={p.id} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5" data-testid={`hidden-${p.id}`}>
                          <span className="truncate text-[#222]/70">{p.content || "Post"}</span>
                          <button onClick={() => unhide(p.id)} className="text-[#245544] font-semibold shrink-0 ml-3">Restore</button>
                        </div>
                      ))}
                      {!mod?.hidden_posts?.length && <p className="text-xs text-[#222]/40">Nothing hidden.</p>}
                    </div>
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><VolumeX size={15}/> Muted accounts ({mod?.muted?.length || 0})</p>
                      {(mod?.muted || []).map(m => (
                        <div key={m.user_id} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5" data-testid={`muted-${m.user_id}`}>
                          <span>{m.name}</span><button onClick={() => unmute(m.user_id)} className="text-[#245544] font-semibold">Unmute</button>
                        </div>
                      ))}
                      {!mod?.muted?.length && <p className="text-xs text-[#222]/40">No muted accounts.</p>}
                    </div>
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2 mb-2"><Ban size={15}/> Blocked accounts ({mod?.blocked?.length || 0})</p>
                      {(mod?.blocked || []).map(m => (
                        <div key={m.user_id} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5" data-testid={`blocked-${m.user_id}`}>
                          <span>{m.name}</span><button onClick={() => unblock(m.user_id)} className="text-[#245544] font-semibold">Unblock</button>
                        </div>
                      ))}
                      {!mod?.blocked?.length && <p className="text-xs text-[#222]/40">No blocked accounts.</p>}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-black/5">
                  <h3 className="font-bold text-lg mb-3" style={{ fontFamily: "Outfit" }}>Reports you submitted</h3>
                  {reports.length === 0 ? <p className="text-xs text-[#222]/40">You haven't reported anything.</p> : (
                    <div className="space-y-2">
                      {reports.map(r => (
                        <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-black/5" data-testid={`report-${r.id}`}>
                          <span className="capitalize">{r.reason.replace("_", " ")} · {r.target_type}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-black/5 capitalize">{(r.status || "submitted").replace("_", " ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
